/**
 * The Seaport action (rulebook p.5–6): one of
 *   draw         — draw 2 cards, then discard 1 (a second step, awaitSeaportDiscard)
 *   takeDiscard  — take the top card of either discard pile
 *   display      — play 1–2 cards to the personal display and draw as many
 *   ship         — play a set on one ship for VP; the ship turns grey
 * Rulings: R-015 (pile exhaustion), R-024 (which card is discarded), R-025 (where shipped cards go).
 */
import { drawCards } from '../cards.js';
import { addVp, afterAction, fail, playerOf, succeed, updatePlayer, } from '../internal.js';
import { MANIFESTS } from '../manifests.js';
export const DISPLAY_MAX = 2;
// ---------------------------------------------------------------------------
// Set validation
// ---------------------------------------------------------------------------
/** Number of "units" a set is worth on the ship's table: cards for identical/different, pairs for pairs. */
export function setSize(set, types) {
    if (types.length === 0)
        return null;
    const counts = new Map();
    for (const t of types)
        counts.set(t, (counts.get(t) ?? 0) + 1);
    switch (set) {
        case 'identical':
            return counts.size === 1 && types.length <= 4 ? types.length : null;
        case 'different':
            return counts.size === types.length && types.length <= 4 ? types.length : null;
        case 'pairs': {
            if ([...counts.values()].some((n) => n !== 2))
                return null;
            return counts.size <= 3 ? counts.size : null;
        }
    }
}
export function shipVp(ship, units) {
    const table = MANIFESTS.ships.find((s) => s.id === ship.id);
    if (!table)
        return 0;
    return (ship.side === 'colour' ? table.colour : table.grey)[String(units)] ?? 0;
}
// ---------------------------------------------------------------------------
// Options (for legalCommands and the client). Wilds are not enumerated.
// ---------------------------------------------------------------------------
export function seaportOptions(state, player) {
    const out = [{ option: 'draw' }];
    if (state.discards[0].length > 0)
        out.push({ option: 'takeDiscard', pile: 0 });
    if (state.discards[1].length > 0)
        out.push({ option: 'takeDiscard', pile: 1 });
    const hand = player.hand ?? [];
    if (hand.length > 0)
        out.push({ option: 'display', cardIds: [hand[0].id] });
    if (hand.length > 1)
        out.push({ option: 'display', cardIds: [hand[0].id, hand[1].id] });
    const byType = new Map();
    for (const c of hand)
        byType.set(c.type, [...(byType.get(c.type) ?? []), c]);
    const types = [...byType.keys()];
    for (const ship of state.board.ships) {
        const table = MANIFESTS.ships.find((s) => s.id === ship.id);
        if (!table)
            continue;
        if (table.set === 'identical') {
            for (const cards of byType.values()) {
                for (let n = 1; n <= Math.min(4, cards.length); n++) {
                    out.push({ option: 'ship', ship: ship.id, cardIds: cards.slice(0, n).map((c) => c.id) });
                }
            }
        }
        else if (table.set === 'different') {
            for (const combo of combinations(types, 4)) {
                out.push({
                    option: 'ship',
                    ship: ship.id,
                    cardIds: combo.map((t) => byType.get(t)[0].id),
                });
            }
        }
        else {
            const pairTypes = types.filter((t) => byType.get(t).length >= 2);
            for (const combo of combinations(pairTypes, 3)) {
                out.push({
                    option: 'ship',
                    ship: ship.id,
                    cardIds: combo.flatMap((t) => byType
                        .get(t)
                        .slice(0, 2)
                        .map((c) => c.id)),
                });
            }
        }
    }
    return out;
}
/** All non-empty combinations of `items` with size 1..max, in a stable order. */
function combinations(items, max) {
    const out = [];
    const walk = (start, acc) => {
        if (acc.length > 0)
            out.push([...acc]);
        if (acc.length === max)
            return;
        for (let i = start; i < items.length; i++)
            walk(i + 1, [...acc, items[i]]);
    };
    walk(0, []);
    return out;
}
// ---------------------------------------------------------------------------
// The action
// ---------------------------------------------------------------------------
export function seaport(state, actor, payload) {
    const p = payload;
    const player = playerOf(state, actor);
    switch (p?.option) {
        case 'draw': {
            const drawn = drawCards(state, 2);
            if (drawn.cards.length === 0)
                return fail('ILLEGAL', 'no cards left to draw');
            const next = updatePlayer(drawn.state, actor, (pl) => ({
                ...pl,
                hand: [...pl.hand, ...drawn.cards],
            }));
            const events = [
                ...drawn.events,
                { type: 'CardsDrawn', player: actor, count: drawn.cards.length },
            ];
            return succeed({ ...next, phase: { kind: 'awaitSeaportDiscard' } }, events);
        }
        case 'takeDiscard': {
            const pile = p.pile;
            if (pile !== 0 && pile !== 1)
                return fail('ILLEGAL', 'pile must be 0 or 1');
            const stack = state.discards[pile];
            const card = stack[stack.length - 1];
            if (!card)
                return fail('ILLEGAL', `discard pile ${pile} is empty`);
            const discards = [...state.discards];
            discards[pile] = stack.slice(0, -1);
            let next = updatePlayer({ ...state, discards }, actor, (pl) => ({
                ...pl,
                hand: [...pl.hand, card],
            }));
            const events = [
                { type: 'CardTakenFromDiscard', player: actor, pile, cardId: card.id },
            ];
            const refilled = refillEmptyDiscards(next);
            next = refilled.state;
            events.push(...refilled.events);
            return succeed(afterAction(next), events);
        }
        case 'display': {
            const ids = p.cardIds;
            if (!Array.isArray(ids) || ids.length < 1 || ids.length > DISPLAY_MAX) {
                return fail('ILLEGAL', `display 1 to ${DISPLAY_MAX} cards`);
            }
            const cards = pickFromHand(player, ids);
            if (!cards)
                return fail('ILLEGAL', 'those cards are not all in your hand');
            const remaining = player.hand.filter((c) => !ids.includes(c.id));
            const drawn = drawCards({ ...state }, cards.length);
            const next = updatePlayer(drawn.state, actor, (pl) => ({
                ...pl,
                hand: [...remaining, ...drawn.cards],
                display: [...pl.display, ...cards],
            }));
            const events = [
                { type: 'CardsDisplayed', player: actor, cardIds: cards.map((c) => c.id) },
                ...drawn.events,
                { type: 'CardsDrawn', player: actor, count: drawn.cards.length },
            ];
            return succeed(afterAction(next), events);
        }
        case 'ship': {
            const ship = state.board.ships.find((s) => s.id === p.ship);
            const table = MANIFESTS.ships.find((s) => s.id === p.ship);
            if (!ship || !table)
                return fail('ILLEGAL', `no ship ${String(p.ship)}`);
            const ids = Array.isArray(p.cardIds) ? p.cardIds : [];
            const cards = pickFromHand(player, ids);
            if (!cards)
                return fail('ILLEGAL', 'those cards are not all in your hand');
            const wilds = Array.isArray(p.wilds) ? p.wilds : [];
            const wildTiles = [];
            for (const w of wilds) {
                const tile = player.forumTiles.find((t) => t.id === w.tileId && t.kind === 'wild-commodity');
                if (!tile ||
                    typeof w.asType !== 'string' ||
                    !MANIFESTS.tiles.commodity.some((c) => c.type === w.asType)) {
                    return fail('ILLEGAL', 'invalid wild-commodity tile');
                }
                if (wildTiles.includes(tile))
                    return fail('ILLEGAL', 'a wild tile can only be used once');
                wildTiles.push(tile);
            }
            const types = [...cards.map((c) => c.type), ...wilds.map((w) => w.asType)];
            const units = setSize(table.set, types);
            if (units === null)
                return fail('ILLEGAL', `that is not a valid ${table.set} set for ${ship.id}`);
            const vp = shipVp(ship, units);
            const wildIds = wildTiles.map((t) => t.id);
            let next = updatePlayer(state, actor, (pl) => ({
                ...pl,
                hand: pl.hand.filter((c) => !ids.includes(c.id)),
                forumTiles: pl.forumTiles.filter((t) => !wildIds.includes(t.id)),
            }));
            // R-025 placeholder: shipped cards go face up on the first discard pile.
            next = {
                ...next,
                discards: [[...next.discards[0], ...cards], next.discards[1]],
                board: {
                    ...next.board,
                    ships: next.board.ships.map((s) => (s.id === ship.id ? { ...s, side: 'grey' } : s)),
                },
                removed: { ...next.removed, forum: [...next.removed.forum, ...wildTiles] },
            };
            next = addVp(next, actor, vp);
            const events = [
                {
                    type: 'CardsShipped',
                    player: actor,
                    ship: ship.id,
                    cardIds: ids,
                    wildTileIds: wildIds,
                    vp,
                },
            ];
            return succeed(afterAction(next), events);
        }
        default:
            return fail('ILLEGAL', "Seaport action needs { option: 'draw' | 'takeDiscard' | 'display' | 'ship' }");
    }
}
/** Second step of 'draw': discard one card from the hand onto either pile (R-024). */
export function seaportDiscard(state, actor, cardId, pile) {
    if (state.phase.kind !== 'awaitSeaportDiscard')
        return fail('WRONG_PHASE', 'no discard is pending');
    if (pile !== 0 && pile !== 1)
        return fail('ILLEGAL', 'pile must be 0 or 1');
    const player = playerOf(state, actor);
    const card = player.hand.find((c) => c.id === cardId);
    if (!card)
        return fail('ILLEGAL', 'that card is not in your hand');
    const discards = [...state.discards];
    discards[pile] = [...discards[pile], card];
    const next = updatePlayer({ ...state, discards }, actor, (pl) => ({
        ...pl,
        hand: pl.hand.filter((c) => c.id !== card.id),
    }));
    return succeed(afterAction(next), [
        { type: 'CardDiscarded', player: actor, pile, cardId: card.id },
    ]);
}
function pickFromHand(player, ids) {
    if (new Set(ids).size !== ids.length)
        return null;
    const cards = [];
    for (const id of ids) {
        const c = player.hand.find((x) => x.id === id);
        if (!c)
            return null;
        cards.push(c);
    }
    return cards;
}
/** Rulebook p.5: an empty discard pile is refilled with the top card of the face-down pile. */
export function refillEmptyDiscards(state) {
    let next = state;
    const events = [];
    for (const pile of [0, 1]) {
        if (next.discards[pile].length > 0)
            continue;
        const drawn = drawCards(next, 1);
        const card = drawn.cards[0];
        if (!card)
            break;
        const discards = [...drawn.state.discards];
        discards[pile] = [card];
        next = { ...drawn.state, discards };
        events.push(...drawn.events, { type: 'DiscardPileRefilled', pile, cardId: card.id });
    }
    return { state: next, events };
}
//# sourceMappingURL=seaport.js.map